import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Input } from './input';
import { formatCurrency } from '../../lib/utils';

/**
 * Searchable service picker with grouping by category.
 * Props:
 *  - servicos: array of {id, nome, preco_base, categoria_nome}
 *  - value: selected servico_id
 *  - onChange: fn(servicoId)
 *  - placeholder?: string
 *  - testId?: string (used as data-testid on trigger button)
 */
export const ServicoPicker = ({ servicos, value, onChange, placeholder = 'Selecione um serviço', testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = servicos.find((s) => s.id === value);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? servicos.filter((s) =>
          s.nome.toLowerCase().includes(q) ||
          (s.categoria_nome || '').toLowerCase().includes(q)
        )
      : servicos;
    const map = {};
    filtered.forEach((s) => {
      const cat = s.categoria_nome || 'Sem categoria';
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [servicos, query]);

  return (
    <div className="min-w-0">
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal h-9"
          title={selected ? `${selected.nome} — ${formatCurrency(selected.preco_base)}` : undefined}
          data-testid={testId}
        >
          <span className="truncate text-left">
            {selected ? (
              <span className="flex items-center gap-2">
                <span className="font-medium">{selected.nome}</span>
                <span className="text-xs text-emerald-700 font-semibold">
                  {formatCurrency(selected.preco_base)}
                </span>
              </span>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-2rem))] p-0" align="start">
        <div className="p-2 border-b border-gray-100 flex items-center gap-2">
          <Search size={14} className="text-gray-400 flex-shrink-0 ml-1" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome ou categoria..."
            className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {grouped.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Nenhum serviço encontrado
            </div>
          ) : (
            grouped.map(([cat, items]) => (
              <div key={cat} className="py-1">
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 sticky top-0">
                  {cat}
                </div>
                {items.map((s) => {
                  const isSelected = value === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        onChange(s.id);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-50' : ''}`}
                      data-testid={testId ? `${testId}-option-${s.id}` : undefined}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Check
                          size={14}
                          className={`text-emerald-600 flex-shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                        />
                        <span className="truncate">{s.nome}</span>
                      </span>
                      <span className="text-xs text-emerald-700 font-semibold flex-shrink-0 tabular-nums">
                        {formatCurrency(s.preco_base)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
    {selected && (
      <p
        className="mt-1 text-[11px] leading-tight text-gray-500 break-words"
        data-testid={testId ? `${testId}-nome-completo` : undefined}
      >
        {selected.nome}
      </p>
    )}
    </div>
  );
};
